import { OrbitControls } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { type ComponentRef, useEffect, useRef } from 'react'

import { useViewingDirection } from '@routes/Panorama/ViewingDirectionContext'

import type { PanoramaImage } from '@state/panorama.ts'

interface CameraControlsProps {
  initialAzimuth?: number
  panoramaImage: PanoramaImage
}

function CameraControls({ initialAzimuth, panoramaImage }: CameraControlsProps) {
  const controlsRef = useRef<ComponentRef<typeof OrbitControls>>(null)
  const { camera } = useThree()
  const { setAzimuthalAngle, polarAngle, setPolarAngle, syncedImageId, setSyncedImageId } = useViewingDirection()

  useEffect(() => {
    if (!controlsRef.current) return

    if (initialAzimuth !== undefined) {
      const dist = camera.position.distanceTo(controlsRef.current.target)
      const y = dist * Math.cos(polarAngle)
      const r_xz = dist * Math.sin(polarAngle) // projected radius on XZ plane

      const x = r_xz * Math.sin(initialAzimuth)
      const z = r_xz * Math.cos(initialAzimuth)

      camera.position.set(x, y, z)
    }

    controlsRef.current.update()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera, initialAzimuth]) // Warning: including polarAngle here would reset position when user pans. Don't include it.

  // Track the angles on each frame
  useFrame(() => {
    if (!controlsRef.current) return

    setAzimuthalAngle(controlsRef.current.getAzimuthalAngle())
    setPolarAngle(controlsRef.current.getPolarAngle())

    if (syncedImageId !== panoramaImage.id) {
      setSyncedImageId(panoramaImage.id)
    }
  })

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping={true}
      dampingFactor={0.05}

      enableZoom={false}
      enablePan={false}
      enableRotate={true}

      minPolarAngle={1.2}
      maxPolarAngle={Math.PI - 1.2}
    />
  )
}

export default CameraControls